/**
 * Experience Search Component
 *
 * Search engine for finding people with specific experience across all projects.
 * Allows filtering by users, therapeutic areas, indications, and free text search.
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Grid,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useReferenceData } from '../hooks/useReferenceData';
import { ContributionSearchRequest, ContributionSearchResult } from '../types';
import projectService from '../services/projectService';
import { LoadingSpinner } from '../../../components/loading/LoadingStates';
import { ErrorDisplay } from '../../../components/errors/ErrorDisplay';
import UserAutocomplete, { User } from '../../../components/shared/UserAutocomplete';

const ExperienceSearch: React.FC = () => {
  const { referenceData } = useReferenceData();

  // Search filters
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [selectedTA, setSelectedTA] = useState<string>('');
  const [selectedIndication, setSelectedIndication] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');

  // Results
  const [results, setResults] = useState<ContributionSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Perform search
  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const request: ContributionSearchRequest = {
        userIds: selectedUsers.length > 0 ? selectedUsers.map(u => u.id) : undefined,
        therapeuticArea: selectedTA || undefined,
        indication: selectedIndication || undefined,
        searchText: searchText.trim() || undefined,
      };

      const data = await projectService.searchContributions(request);
      setResults(data);
    } catch (err: any) {
      setError(err.message || 'Failed to search contributions');
      console.error('Error searching contributions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Clear all filters
  const handleClear = () => {
    setSelectedUsers([]);
    setSelectedTA('');
    setSelectedIndication('');
    setSearchText('');
    setResults([]);
    setHasSearched(false);
  };

  // Get TA name from code
  const getTherapeuticAreaName = (code: string): string => {
    const ta = referenceData?.therapeuticAreas.find(ta => ta.code === code);
    return ta?.name || code;
  };

  // Get indication name from code
  const getIndicationName = (code: string): string => {
    const indication = referenceData?.indications.find(ind => ind.code === code);
    return indication?.name || code;
  };

  // Get filtered indications based on selected TA
  const filteredIndications = selectedTA
    ? referenceData?.indications.filter(ind => ind.therapeuticArea === selectedTA) || []
    : referenceData?.indications || [];

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Search on Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'white', mb: 1 }}>
          Find Expertise
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          Search for people with specific experience across all projects
        </Typography>
      </Box>

      {/* Search Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          {/* User Search */}
          <Grid size={{ xs: 12, md: 6 }}>
            <UserAutocomplete
              value={selectedUsers}
              onChange={setSelectedUsers}
              label="Search by Person"
              placeholder="Type name to search..."
            />
          </Grid>

          {/* Therapeutic Area */}
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Therapeutic Area</InputLabel>
              <Select
                value={selectedTA}
                onChange={(e: SelectChangeEvent) => {
                  setSelectedTA(e.target.value);
                  setSelectedIndication(''); // Reset indication when TA changes
                }}
                label="Therapeutic Area"
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {referenceData?.therapeuticAreas.map((ta) => (
                  <MenuItem key={ta.code} value={ta.code}>
                    {ta.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Indication */}
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Indication</InputLabel>
              <Select
                value={selectedIndication}
                onChange={(e: SelectChangeEvent) => setSelectedIndication(e.target.value)}
                label="Indication"
                disabled={!selectedTA}
              >
                <MenuItem value="">
                  <em>All</em>
                </MenuItem>
                {filteredIndications.map((ind) => (
                  <MenuItem key={ind.code} value={ind.code}>
                    {ind.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Free Text Search */}
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Search activities, descriptions, etc."
              placeholder="Search activities, project names, clients, roles, and descriptions..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                endAdornment: searchText && (
                  <IconButton size="small" onClick={() => setSearchText('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Tooltip title="Clear all filters">
                <IconButton onClick={handleClear} disabled={loading}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Search">
                <IconButton onClick={handleSearch} disabled={loading} color="primary">
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Results */}
      {loading ? (
        <LoadingSpinner message="Searching contributions..." />
      ) : error ? (
        <ErrorDisplay
          title="Search failed"
          error={error}
          onRetry={handleSearch}
        />
      ) : hasSearched ? (
        results.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No results found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search filters
            </Typography>
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Person</strong></TableCell>
                    <TableCell><strong>Role</strong></TableCell>
                    <TableCell><strong>Project / Client</strong></TableCell>
                    <TableCell><strong>TA / Indication</strong></TableCell>
                    <TableCell><strong>Activity</strong></TableCell>
                    <TableCell><strong>Contribution</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result) => (
                    <TableRow key={result.contributionId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="text.primary">
                          {result.teamMemberName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.primary">
                          {result.roleOnProject}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="text.primary">
                          {result.projectName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {result.client}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }} color="text.primary">
                          {getTherapeuticAreaName(result.therapeuticArea)}
                          {'\n'}
                          {getIndicationName(result.indication)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.primary">
                          {result.activity}
                        </Typography>
                        {result.subActivity && (
                          <Typography variant="caption" color="text.secondary">
                            {result.subActivity}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.primary" sx={{ maxWidth: 300 }}>
                          {result.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.primary">
                          {formatDate(result.date)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary */}
            <Box sx={{ mt: 2, textAlign: 'right' }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Found {results.length} contribution{results.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </>
        )
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Start searching
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the filters above to find people with specific experience
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ExperienceSearch;
